import { useSelector, useDispatch } from 'react-redux'
import { useMemo, useCallback } from 'react'
import { setDateToMidday } from '../utils'
import moment from 'moment'
import { api } from '../services/api'
import { showSuccess, showError } from '../actions/misc/misc.actions'
import { isVipMembershipType } from '../utils/constants'
import { matchesFacility } from '../utils/facilityMatch'

const useSelectedHousehold = () => {
	const household = useSelector((state) => state.specific.households.selected)
	const loading = useSelector((state) => state.specific.households.loading)
	const dispatch = useDispatch()

	const visits = useSelector((state) => {
		return state.specific.visits.visits
	})

	const training_enrollments = useSelector((state) => {
		return state.specific.visits.training_enrollments
	})

	const selectedFilters = useSelector((state) => state.specific.visits.selectedHouseholdFilters)

	const householdVisits = useMemo(() => {
		if (!household) {
			return []
		}

		const { _id } = household
		const householdVisits = visits?.filter((visit) => visit.household_id === _id) || []
		const trainingVisits = training_enrollments?.filter((visit) => visit.household_id === _id) || []

		const allVisits = [...householdVisits, ...trainingVisits].sort((a, b) => {
			const aDate = moment(a.startDate)
			const bDate = moment(b.startDate)
			if (aDate.isBefore(bDate)) {
				return 1
			}
			if (aDate.isAfter(bDate)) {
				return -1
			}
			return 0
		})

		return allVisits
	}, [household, visits, training_enrollments])

	const filteredHouseholdVisits = useMemo(() => {
		let visitsFiltered = [...householdVisits]
		if (selectedFilters['appointment_type'] && selectedFilters['appointment_type'].length) {
			visitsFiltered = visitsFiltered.filter((v) => selectedFilters['appointment_type'].includes(v.type))
		}
		if (selectedFilters['appointment_status'] && selectedFilters['appointment_status'].length) {
			visitsFiltered = visitsFiltered.filter((v) =>
				v.status && v.status.id ? selectedFilters['appointment_status'].includes(v.status.id) : false
			)
		}
		return visitsFiltered
	}, [selectedFilters, visits])

	const membershipsAvailableForDate = useCallback(
		(date, facilityId) => {
			if (!household) {
				return []
			}

			const { memberships = [] } = household

			const output = {}

			memberships.forEach((membership) => {
				const { deactivated, end_date, start_date, expired, auto_renew, upcoming } = membership

				if (facilityId && !matchesFacility(membership, facilityId)) {
					return
				}

				if (deactivated || expired) {
					return false
				}

				const end = moment(end_date).endOf('day')
				const start = moment(start_date).startOf('day')
				const day = setDateToMidday(moment(date))

				const dayIsBewteen = day.isBetween(start, end)

				if (upcoming && dayIsBewteen) {
					output[membership.type] = { validBy: 'upcoming', type: membership.type, start, end }
				}

				if (auto_renew && !dayIsBewteen) {
					output[membership.type] = { validBy: 'auto_renew', type: membership.type, start, end }
				}

				output[membership.type] = { validBy: 'current', type: membership.type, start, end }
			})

			return output
		},
		[household]
	)

	const currentMembership = useMemo(() => {
		if (!household) {
			return null
		}

		const { memberships = [] } = household

		const current = memberships.find((membership) => {
			const { deactivated, end_date, expired } = membership
			return (
				!deactivated && !expired && moment(end_date).startOf('day').isSameOrAfter(moment().startOf('day'))
			)
		})

		return current || null
	}, [household])

	const addOnBankValue = useMemo(() => household?.addon_bank || 0, [household])

	const daycareCredits = useMemo(() => household?.credits?.daycare || [], [household])

	const isVip = useMemo(() => {
		if (!household || !currentMembership) {
			return false
		}

		const { type } = currentMembership
		return isVipMembershipType(type)
	}, [household, currentMembership])

	const isPetCare = useMemo(() => {
		if (!household || !currentMembership) {
			return false
		}
		const { type } = currentMembership
		return type.includes('petcare') || type.includes('pet_care')
	}, [household])

	const isMonthly = useMemo(() => {
		if (!household || !currentMembership) {
			return false
		}
		const { type } = currentMembership
		return type.includes('monthly')
	}, [household])

	const isAnnual = useMemo(() => {
		if (!household || !currentMembership) {
			return false
		}
		const { type } = currentMembership
		return type.includes('annual')
	}, [household])

	const hasValidCardOnFile = useMemo(() => {
		return household && household.stripe && household.stripe.data.length
	}, [household])

	const isVipOrPetCareOnDate = useCallback(
		(date, facilityId) => {
			const output = { vip: false, petcare: false }
			if (!date) {
				return output
			}

			const validMembershipsForDate = membershipsAvailableForDate(date, facilityId)
			const memTypes = Object.keys(validMembershipsForDate)

			if (memTypes.length === 0) {
				return output
			}

			memTypes.forEach((type) => {
				if (isVipMembershipType(type)) {
					output.vip = true
				}
				if (type.includes('petcare') || type.includes('pet_care')) {
					output.petcare = true
				}
			})

			return output
		},
		[membershipsAvailableForDate]
	)

	const giftCardBalance = useMemo(() => {
		if (!household) {
			return 0
		}

		const { giftcard_balance = 0 } = household

		return giftcard_balance
	}, [household])

	const accountCreditBalance = useMemo(() => {
		if (!household) {
			return 0
		}

		const { credit = 0 } = household

		return credit
	}, [household])

	/**
	 * Total credit available to the household (gift card balance + account credit balance)
	 */
	const totalCreditAvailable = useMemo(() => {
		return giftCardBalance + accountCreditBalance
	}, [giftCardBalance, accountCreditBalance])

	const addDaypassesToHousehold = useCallback(
		async (body) => {
			const payload = {
				success: false,
				data: [],
				errors: []
			}

			if (!body || !body.household_id) {
				payload.errors = ['Invalid parameters']
				return payload
			}

			try {
				const { data } = await api.post(`/clients/${body.household_id}/addDaypasses/`, body)
				if (data.data && data.data.length > 0) {
					dispatch({
						type: 'ADD_SELECTED_HOUSEHOLD_DAYPASSES',
						payload: {
							household_id: body.household_id,
							daypasses: data.data
						}
					})
					payload.success = true
					payload.data = data.data[0]
				}
				if (data.errors && data.errors.length) {
					payload.errors = data.errors
				}
			} catch (e) {
				payload.errors = e.data?.errors || e.message || ['An error occurred while adding daypasses']
			}

			if (payload.success) {
				dispatch(showSuccess('Daypasses added successfully.'))
			}
			return payload
		},
		[household]
	)
	const useDaypassesFromHousehold = useCallback(
		async ({ number_to_use, household_id }) => {
			const payload = {
				success: false,
				data: [],
				errors: []
			}

			if (!number_to_use || !household_id) {
				payload.errors = ['Invalid parameters']
				return payload
			}

			try {
				const { data } = await api.patch(`/clients/${household_id}/useDaypasses/`, { number_to_use })
				if (data.data && data.data.length > 0) {
					dispatch({
						type: 'REMOVE_SELECTED_HOUSEHOLD_DAYPASSES',
						payload: {
							household_id,
							daypass_ids: data.data
						}
					})
					payload.success = true
					payload.data = data.data
				}
				if (data.errors && data.errors.length) {
					payload.errors = data.errors
				}
			} catch (e) {
				payload.errors = e.data?.errors || e.message || ['An error occurred while removing daypasses']
			}

			if (payload.success) {
				dispatch(showSuccess('Daypasses removed successfully.'))
			}
			return payload
		},
		[household]
	)

	return {
		household,
		currentMembership,
		isVip,
		isPetCare,
		isMonthly,
		isAnnual,
		addOnBankValue,
		hasValidCardOnFile,
		daycareCredits,
		membershipsAvailableForDate,
		isVipOrPetCareOnDate,
		householdVisits,
		loading,
		filteredHouseholdVisits,
		giftCardBalance,
		accountCreditBalance,
		totalCreditAvailable,
		addDaypassesToHousehold,
		useDaypassesFromHousehold
	}
}

export default useSelectedHousehold
